import { useEffect, useState } from 'react';
import { useSelector } from '@xstate/store/react';
import { Form } from 'react-aria-components';
import { z } from 'zod/v4';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from '@tanstack/react-query';
import { createStore } from '@xstate/store';

import { useUser } from 'context/user';
import { useI18n } from 'context/i18n';
import { useLocale } from 'hooks/useLocale';
import { useLink } from 'hooks/useLink';
import { useForm } from 'hooks/use-form';
import { Button } from 'components/ui/button';
import { Dialog, DialogHeader, DialogRoot } from 'components/ui/dialog';
import { TextField } from 'components/ui/text-field';
import { ProtectionDataModal } from 'components/protectionDataModal';
import { Checkbox } from 'components/ui/checkbox';
import { PrivacyPolicyText } from 'components/legalTexts';
import { RecaptchaWrapper } from 'components/recaptcha-wrapper';
import { createAccountV2, loginV2, recoverPassword } from 'actions/users';
import { getAppError } from 'utils/error';
import { storage } from 'utils/web-storage';
import { getDomainValue, isDomain } from 'utils/domain';
import { emailSchema, privacySchema, requiredStringSchema } from 'utils/validation';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { browserToSHA256 } from 'utils/browser';

export type PossibleSection = 'login' | 'register' | 'password';

const accountRoute = getDomainValue({
  camper: '/myaccount',
  camperlab: '/myaccount',
  nnormal: '/myaccount/nnormal-world',
});

export const store = createStore({
  context: {
    isOpen: false,
    section: 'login' as PossibleSection,
    redirect: true,
    email: '',
  },
  on: {
    open: (context, event: Parameters<OpenAccountFn>[0]) => {
      context.isOpen = true;
      context.section = event?.section ?? context.section ?? 'login';
      context.redirect = event?.redirect ?? true;
    },
    close: (context) => {
      context.isOpen = false;
      context.section = 'login';
      context.redirect = true;
    },
    change_section: (context, event: { section: PossibleSection }) => {
      context.section = event.section;
    },
    change_email: (context, event: { email: string }) => {
      context.email = event.email;
    },
  },
});

const defaultSchema = z.object({
  email: emailSchema,
  password: requiredStringSchema,
});

type LoginFormValues = z.infer<typeof defaultSchema>;
// TODO: cuando quitemos el useUser context, en vez de handleGetUserData, invalidar el query de user
function LoginForm() {
  const { t } = useI18n();
  const to = useLink();
  const { locale } = useLocale();
  const { handleGetUserData }: any = useUser();
  const shouldRedirect = useSelector(store, (state) => state.context.redirect);
  const userInfo = useMutation({
    mutationFn: () => handleGetUserData(locale),
    onSuccess: (data) => {
      store.send({ type: 'close' });
    },
    onError: (error) => {
      console.error('Error fetching user data:', error);
    },
  });
  const login = useMutation({
    mutationFn: (data: LoginFormValues) => loginV2(data),
    onSuccess: async (data) => {
      storage.local.set('token', data.token);
      storage.local.set('refresh_token', data.refreshToken);
      if (data.info) {
        storage.local.set('shippingForm', {
          ...data.info,
          name: data.info.username,
          telephone: data.info.telephone && data.info.telephone.length ? data.info.telephone.split(' ').splice(1).join('') : '',
        });

        const hashedEmail = await browserToSHA256(data.info.email?.toLowerCase());
        dataLayerHandleEvent({ event: 'mailShadowed', mailSHA256: hashedEmail });
        dataLayerHandleEvent({ event: 'login', method: 'email' });
      }
      if (shouldRedirect) {
        window.location.assign(to(accountRoute));
        return;
      }
      userInfo.mutate();
    },
  });

  const defaultEmail = useSelector(store, (state) => state.context.email);

  const { handleSubmit, validate } = useForm({ schema: defaultSchema });

  const isPending =
    shouldRedirect ? ['pending', 'success'].includes(login.status) : ['pending'].includes(login.status) || ['pending'].includes(userInfo.status);
  const requestError = userInfo?.error ? getAppError((userInfo.error as unknown as { data: TODO }).data.error as unknown)?.message : login?.error?.message;
  return (
    <div className="flex flex-col items-center gap-4">
      {isDomain('camper') ?
        <img src="/assets-new/my-account-hand.gif" alt="welcome login" width={90} height={90} />
      : null}
      <p className="mb-0 text-2xl nnormal:font-secondary">{t('mi.cuenta', 'login.titulo.bienvenido', 'Good to see you again')}</p>
      <p className="mb-0 text-sm">{t('mi.cuenta', 'identificate.acceder')}</p>
      <Form
        onSubmit={(e) => {
          login.mutate(handleSubmit(e));
        }}
        className="flex w-full flex-col gap-2"
      >
        <TextField
          variant="box"
          label={t('mi.cuenta', 'mi.cuenta.email').replace(':', '')}
          name="email"
          type="email"
          defaultValue={defaultEmail}
          validate={(value) => validate('email', value)}
          onChange={(value) => {
            store.send({ type: 'change_email', email: value });
          }}
        />
        <TextField variant="box" label={t('mi.cuenta', 'password')} name="password" type="password" validate={(value) => validate('password', value)} />
        <p className="text-error empty:hidden">{requestError}</p>
        <Button
          type="button"
          variant="text"
          className="text-xs [&_span]:underline"
          onPress={() => {
            store.send({ type: 'change_section', section: 'password' });
          }}
        >
          {t('mi.cuenta', 'forgot.your.password', 'Forgot your password?')}
        </Button>
        <Button isPending={isPending} className="nnormal:font-secondary" type="submit">
          {t('mi.cuenta', 'identificate')}
        </Button>
        <p
          className="mx-auto mb-2 mt-10 max-w-60 text-center text-xs"
          dangerouslySetInnerHTML={{
            __html: t('mi.cuenta', 'crear.cuenta.detalles', 'Don’t have an account?<br/>Create an account to reap the benefits of a Camper account.'),
          }}
        />
        <Button variant="outline" className="nnormal:font-secondary" onPress={() => store.send({ type: 'change_section', section: 'register' })}>
          {t('mi.cuenta', 'create.una.cuenta')}
        </Button>
      </Form>
    </div>
  );
}

const passwordFormSchema = defaultSchema.omit({
  password: true,
});

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

function RecoverPasswordForm() {
  const { t } = useI18n();
  const { locale } = useLocale();
  const defaultEmail = useSelector(store, (state) => state.context.email);
  const { handleSubmit, validate } = useForm({ schema: passwordFormSchema });

  const recover = useMutation({
    mutationFn: (data: PasswordFormValues) => {
      return recoverPassword({
        email: data.email,
        profile: locale,
      });
    },
  });

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="mb-0 text-center text-2xl nnormal:font-secondary">{t('mi.cuenta', 'recuperar.password.titulo', 'Retrieve your password')}</p>
      <p className="mb-0 text-center text-sm">
        {t('mi.cuenta', 'recuperar.password.descripcion.1', 'Enter the email address you used to register on Camper.com')}
      </p>
      <p className="mb-0 text-center text-sm">
        {t('mi.cuenta', 'recuperar.password.descripcion.2', 'We will send the link to access your account to the same email address.')}
      </p>
      <Form onSubmit={(e) => recover.mutate(handleSubmit(e))} className="flex w-full flex-col gap-2">
        <TextField
          variant="box"
          label={t('mi.cuenta', 'mi.cuenta.email').replace(':', '')}
          name="email"
          type="email"
          defaultValue={defaultEmail}
          onChange={(value) => {
            store.send({ type: 'change_email', email: value });
          }}
          validate={(value) => validate('email', value, t('validation', 'required.email', 'Please input your email'))}
        />
        <p className="text-error empty:hidden">{recover.error?.message}</p>
        {recover.status === 'success' ?
          <p className="mb-0 text-sm text-success">{t('mi.cuenta', 'recuperar.password.peticion.enviada')}</p>
        : null}
        <Button isPending={recover.status === 'pending'} type="submit" className="nnormal:font-secondary">
          {t('mi.cuenta', 'recuperar.password', 'Retrieve your password')}
        </Button>
        <Button
          type="button"
          variant="text"
          className="text-xs [&_span]:underline"
          onPress={() => {
            store.send({ type: 'change_section', section: 'login' });
          }}
        >
          {t('mi.cuenta', 'recuperar.password.volver', 'Volver')}
        </Button>
      </Form>
    </div>
  );
}

const registerSchema = defaultSchema
  .extend({
    confirm: requiredStringSchema,
    privacy: privacySchema,
    newsletter: z.stringbool().optional().default(false),
  })
  .refine((data) => data.password === data.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

function RegisterForm() {
  const { t } = useI18n();
  const { locale } = useLocale();
  const { handleGetUserData }: any = useUser();
  // const canUseRecaptcha = useFeature('captchaEnabled');
  const shouldRedirect = useSelector(store, (state) => state.context.redirect);
  const to = useLink();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const isRepeatInvalid = repeatPassword !== '' && password !== repeatPassword;

  const userInfo = useMutation({
    mutationFn: () => handleGetUserData(locale),
    onSuccess: (data) => {
      store.send({ type: 'close' });
    },
    onError: (error) => {
      console.error('Error fetching user data:', error);
    },
  });

  const register = useMutation({
    mutationFn: async (data: RegisterFormValues) => {
      const recaptchaToken = await executeRecaptcha?.('create_account');
      const response = await createAccountV2({
        locale,
        email: data.email,
        password: data.password,
        captchaToken: recaptchaToken ?? null,
        newsletter: data.newsletter,
      });
      return {
        response,
        requestData: data,
      };
    },
    onSuccess: async ({ response, requestData }) => {
      storage.local.set('token', response.token);
      storage.local.set('refresh_token', response.refreshToken);
      if (response.info) {
        storage.local.set('shippingForm', {
          ...response.info,
          name: response.info.username,
          telephone: response.info?.telephone && response.info?.telephone.length ? response.info?.telephone.split(' ').splice(1).join('') : '',
        });
      }
      const hashedEmail = await browserToSHA256(response.info?.email?.toLowerCase() ?? requestData.email.toLowerCase());
      dataLayerHandleEvent({ event: 'mailShadowed', mailSHA256: hashedEmail });
      dataLayerHandleEvent({ event: 'sign_up', method: 'email' });

      if (shouldRedirect) {
        window.location.assign(to(accountRoute));
        return;
      }
      userInfo.mutate();
    },
  });

  const { handleSubmit, validate } = useForm({ schema: registerSchema });

  const isPending =
    shouldRedirect ? ['pending', 'success'].includes(register.status) : ['pending'].includes(register.status) || ['pending'].includes(userInfo.status);
  const requestError = userInfo?.error ? getAppError((userInfo.error as unknown as TODO).data.error as unknown)?.message : register?.error?.message;
  return (
    <div className="flex flex-col items-center gap-4">
      <p className="mb-0 text-center text-2xl nnormal:font-secondary">{t('mi.cuenta', 'mi.cuenta.titulo')}</p>
      <p className="mb-0 text-center text-sm">{t('mi.cuenta', 'info.registro')}</p>

      <Form autoComplete="off" onSubmit={(e) => register.mutate(handleSubmit(e))} className="flex w-full flex-col gap-2">
        <TextField
          variant="box"
          label={t('mi.cuenta', 'mi.cuenta.email').replace(':', '')}
          autoComplete="off"
          name="email"
          type="email"
          validate={(value) => validate('email', value, t('validation', 'required.email', 'Please input your email'))}
          onChange={(value) => {
            store.send({ type: 'change_email', email: value });
          }}
        />
        <TextField
          variant="box"
          label={t('mi.cuenta', 'password')}
          autoComplete="off"
          name="password"
          type="password"
          onChange={(value) => setPassword(value)}
          validate={(value) => validate('password', value, t('validation', 'required.password', 'Please input your password'))}
        />
        <TextField
          variant="box"
          label={t('mi.cuenta', 'repite.la.contrasena').replace(':', '')}
          name="confirm"
          type="password"
          onChange={(repeatPassword) => setRepeatPassword(repeatPassword)}
          isInvalid={isRepeatInvalid}
          autoComplete="false"
          errorMessage={isRepeatInvalid ? t('validation', 'password.no.match') : undefined}
        />
        <div className="my-2 flex items-center justify-center">
          <ProtectionDataModal origin="registro.myaccount" overlay />
        </div>
        <Checkbox
          name="privacy"
          isRequired
          validate={(value) => validate('privacy', value, t('validation', 'accept.policy', 'You must accept the terms and conditions'))}
          boxClassName="size-5"
        >
          <PrivacyPolicyText className="text-center text-base lab:text-sm lab:lg:text-xs [&_a]:font-medium" />
        </Checkbox>
        <Checkbox name="newsletter" boxClassName="size-5">
          <span className="text-center text-base lab:text-sm lab:lg:text-xs [&_a]:font-medium">{t('compra.formulario', 'informacion.comercial')}</span>
        </Checkbox>
        <p className="text-error empty:hidden">{requestError}</p>
        <Button isPending={isPending} type="submit" className="nnormal:font-secondary">
          {t('mi.cuenta', 'crear.cuenta')}
        </Button>
        <Button
          type="button"
          variant="text"
          className="text-xs [&_span]:underline"
          onPress={() => {
            store.send({ type: 'change_section', section: 'login' });
          }}
        >
          {t('mi.cuenta', 'volver.al.login').replace('&lt;', '<')}
        </Button>
      </Form>
    </div>
  );
}

export type OpenAccountFn = (options?: { section?: PossibleSection; redirect?: boolean }) => void;

export function MyAccountModal() {
  const { t } = useI18n();
  const isOpen = useSelector(store, (state) => state.context.isOpen);
  const section = useSelector(store, (state) => state.context.section);

  useEffect(() => {
    window.openAccountModal = (options) => {
      store.send({ type: 'open', section: options?.section ?? 'login', redirect: options?.redirect ?? true });
    };
    return () => {
      window.openAccountModal = undefined;
    };
  }, []);

  return (
    <DialogRoot
      isOpen={isOpen}
      onOpenChange={() => {
        store.send({ type: 'close' });
      }}
      isDismissable
      showOverlay
    >
      <Dialog className="flex h-dvh w-dvw flex-col shadow sm:h-auto sm:w-[360px] sm:min-w-0">
        <DialogHeader
          label={
            {
              login: t('mi.cuenta', 'identificate'),
              register: t('compra.formulario', 'create.account'),
              password: t('mi.cuenta', 'recuperar.password.titulo'),
            }[section]
          }
          hidden
        />
        <div className="mt-1 flex flex-grow flex-col">
          {
            {
              login: <LoginForm />,
              register: (
                <RecaptchaWrapper force={true}>
                  <RegisterForm />
                </RecaptchaWrapper>
              ),
              password: <RecoverPasswordForm />,
            }[section]
          }
        </div>
      </Dialog>
    </DialogRoot>
  );
}
